import { Button } from '@hover/blueprint';
import { iArrowLeft } from '@hover/icons';
import React, { useContext, useMemo, useState } from 'react';

import { CardHeader } from 'src/modules/designStudio/components/DesignMenu/CardHeader';
import { CheckList } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilterSelector/CheckList';
import { IconButtonCheckList } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilterSelector/IconButtonCheckList';
import {
  createBrandCheckListItems,
  createColorCheckListItems,
  createProductTypeCheckListItems,
  createSimpleCheckListItems,
  treatCheckListSelectionAsRadioSelection,
  treatCheckListSelectionAsRadioSelectionForColors,
} from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilterSelector/utils';
import {
  TestIds,
  TestLabels,
  TestPageIds,
} from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import { useProductRankings } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/useProductRankings';
import { ScrollMenu } from 'src/modules/designStudio/components/DesignMenu/ScrollMenu';
import {
  createEmptyFilterOptions,
  deriveFilterOptions,
  filterProducts,
  sortColors,
} from 'src/modules/designStudio/helpers/productSelection/productFilterUtils';
import type {
  Product,
  ProductCategory,
  ProductFilter,
  ProductTypeLabel,
  VariationColorGroup,
} from 'src/modules/designStudio/helpers/productSelection/types';
import { productCatalogContext } from 'src/modules/designStudio/providers';
import {
  Accordion,
  AccordionItem,
} from 'src/shared/components/Accordion/Accordion';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

export type FilteredSelectorParams = {
  category: ProductCategory;
  selectedFilter: ProductFilter;
  onUserChangedFilterSelection: (filter: ProductFilter) => void;
  onBack: () => void;
  onView: () => void;
  showProductTypes: boolean;
  filterSelectorContext: 'all' | 'color';
};

export const FilterSelector = ({
  category,
  selectedFilter,
  onUserChangedFilterSelection,
  onBack,
  onView,
  showProductTypes,
  filterSelectorContext,
}: FilteredSelectorParams) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { pcDataStore } = useContext(productCatalogContext);
  const { rankProviders } = useProductRankings();

  const [draftFilter, setDraftFilter] = useState<ProductFilter>(selectedFilter);

  const supportedFilterOptions = useMemo(() => {
    if (!pcDataStore) {
      return createEmptyFilterOptions(category);
    }
    const options = deriveFilterOptions(category, draftFilter, pcDataStore);
    rankProviders(draftFilter.productType, options.providers);
    sortColors(options.colorGroups);
    return options;
  }, [draftFilter, category, pcDataStore, rankProviders]);

  const numDraftProducts: number = useMemo(() => {
    if (!pcDataStore) {
      return 0;
    }
    const products: Product[] = filterProducts(
      draftFilter,
      pcDataStore.getProducts(draftFilter.productType),
    );
    return products.reduce((n, product) => n + product.variations.length, 0);
  }, [draftFilter, pcDataStore]);

  const defaultIndex = useMemo(
    () =>
      filterSelectorContext === 'color'
        ? [showProductTypes ? 1 : 0] // when showProductTypes = true, color is first in accordion list
        : [0, 1, 2, 3, 4, 5],
    [filterSelectorContext, showProductTypes],
  );

  const actions = {
    onTypeChanged: (ids: string[]) => {
      /**
       * The ids provided here come from a IconCheckList. In a check
       * you can select more than one item, or have non selected, and the ids provided reflect this.
       * The product type is meant to behave more like radio buttons. We must always have a type selected
       * and only one type can be selected.
       *
       * Therefore, we need to make modifications on the ids passed in as the ids passed in may have have
       * no ids selected or more than one.
       */
      setDraftFilter({
        productType: parseInt(
          treatCheckListSelectionAsRadioSelection(
            ids,
            `${draftFilter.productType}`,
          ),
          10,
        ) as ProductTypeLabel,
        providers: [],
        colorGroups: [],
        sizes: [],
        styles: [],
        textures: [],
      });
    },
    onColorChanged: (ids: string[]) => {
      setDraftFilter({
        ...draftFilter,
        colorGroups: treatCheckListSelectionAsRadioSelectionForColors(
          ids,
          draftFilter.colorGroups.length === 1
            ? draftFilter.colorGroups[0]
            : undefined,
        ) as VariationColorGroup[],
      });
    },
    onBrandChanged: (ids: string[]) => {
      setDraftFilter({
        ...draftFilter,
        providers: ids,
      });
    },
    onStyleChanged: (ids: string[]) => {
      setDraftFilter({
        ...draftFilter,
        styles: ids,
      });
    },
    onTextureChanged: (ids: string[]) => {
      setDraftFilter({
        ...draftFilter,
        textures: ids,
      });
    },
    onSizeChanged: (ids: string[]) => {
      setDraftFilter({
        ...draftFilter,
        sizes: ids,
      });
    },
    onView: () => {
      onUserChangedFilterSelection(draftFilter);
      onView();
    },
  };

  return (
    <ScrollMenu.Menu data-testid={TestPageIds.FILTER}>
      <ScrollMenu.Header>
        <CardHeader
          headerText="Filter by"
          leftActionHandler={onBack}
          leftIcon={iArrowLeft}
          leftIconId={TestIds.BACK_BUTTON}
          m={0}
        />
      </ScrollMenu.Header>
      <ScrollMenu.Body>
        <Accordion defaultIndex={defaultIndex}>
          {showProductTypes && (
            <AccordionItem
              dataTestId={TestIds.ACCORDION_TYPE_BUTTON}
              title={t('productCatalogMenu.filter.type')}
            >
              <IconButtonCheckList
                items={createProductTypeCheckListItems(
                  supportedFilterOptions,
                  draftFilter,
                )}
                onSelectedIdsChanged={actions.onTypeChanged}
                testId={TestIds.ACCORDION_TYPE_CONTENT}
              />
            </AccordionItem>
          )}
          {supportedFilterOptions.colorGroups.length !== 0 && (
            <AccordionItem
              dataTestId={TestIds.ACCORDION_COLOR_BUTTON}
              title={t('productCatalogMenu.filter.color')}
            >
              <IconButtonCheckList
                items={createColorCheckListItems(
                  supportedFilterOptions,
                  draftFilter,
                )}
                onSelectedIdsChanged={actions.onColorChanged}
                testId={TestIds.ACCORDION_COLOR_CONTENT}
              />
            </AccordionItem>
          )}
          {supportedFilterOptions.providers.length !== 0 && (
            <AccordionItem
              dataTestId={TestIds.ACCORDION_BRAND_BUTTON}
              title={t('productCatalogMenu.filter.brand')}
            >
              <CheckList
                items={createBrandCheckListItems(
                  supportedFilterOptions,
                  draftFilter,
                  pcDataStore,
                )}
                onSelectedIdsChanged={actions.onBrandChanged}
                testId={TestIds.ACCORDION_BRAND_CONTENT}
              />
            </AccordionItem>
          )}

          {supportedFilterOptions.styles.length !== 0 && (
            <AccordionItem
              dataTestId={TestIds.ACCORDION_STYLE_BUTTON}
              title={t('productCatalogMenu.filter.style')}
            >
              <CheckList
                items={createSimpleCheckListItems(
                  supportedFilterOptions.styles,
                  draftFilter.styles,
                  TestLabels.FILTER_STYLE_OPTION,
                )}
                onSelectedIdsChanged={actions.onStyleChanged}
                testId={TestIds.ACCORDION_STYLE_CONTENT}
              />
            </AccordionItem>
          )}

          {supportedFilterOptions.textures.length !== 0 && (
            <AccordionItem
              dataTestId={TestIds.ACCORDION_TEXTURE_BUTTON}
              title={t('productCatalogMenu.filter.texture')}
            >
              <CheckList
                items={createSimpleCheckListItems(
                  supportedFilterOptions.textures,
                  draftFilter.textures,
                  TestLabels.FILTER_TEXTURE_OPTION,
                )}
                onSelectedIdsChanged={actions.onTextureChanged}
                testId={TestIds.ACCORDION_TEXTURE_CONTENT}
              />
            </AccordionItem>
          )}

          {supportedFilterOptions.sizes.length !== 0 && (
            <AccordionItem
              dataTestId={TestIds.ACCORDION_SIZE_BUTTON}
              title={t('productCatalogMenu.filter.size')}
            >
              <CheckList
                items={createSimpleCheckListItems(
                  supportedFilterOptions.sizes,
                  draftFilter.sizes,
                  TestLabels.FILTER_SIZE_OPTION,
                )}
                onSelectedIdsChanged={actions.onSizeChanged}
                testId={TestIds.ACCORDION_SIZE_CONTENT}
              />
            </AccordionItem>
          )}
        </Accordion>
      </ScrollMenu.Body>
      <ScrollMenu.Footer>
        <Button
          data-testid={TestIds.APPLY_FILTER}
          height={400}
          onClick={actions.onView}
        >
          {`${t('productCatalogMenu.filter.view')}${
            numDraftProducts === 0
              ? ''
              : ` ${numDraftProducts.toLocaleString()} products`
          }`}
        </Button>
      </ScrollMenu.Footer>
    </ScrollMenu.Menu>
  );
};
